// The handover flow of plan §6 as a pure reducer, so every path is tested without a DOM and the
// same machine runs on the door kiosk and on a teacher's phone (roaming handover, §6 step 8).
// Async checks (PINs, codes, QR passes) run in the component and are fed back as actions.
import { buildPreImage, allowedAdults, restrictionsFor, siblings, type CollectingAdult, type Roster } from "@vavr/client";
import { uuidv7, type AuthMethod, type FaceBox, type ID_TYPES, type PreImage } from "@vavr/core";

export type Direction = "pickup" | "dropoff";
export type SignaturePolicy = "required" | "optional" | "off";
export type IdType = (typeof ID_TYPES)[number];
export type Child = Roster["children"][number];
export type Restriction = Roster["restrictions"][number];

export type Step =
  | "find" | "siblings" | "who" | "adult_pin" | "code" | "someone" | "witness" | "typed_name" | "sign" | "sealing";

/** Why a second staff PIN is being asked for. */
export type WitnessPurpose = "unlisted_adult" | "cannot_sign" | "typed_name";

export type SignatureState = "none" | "drawn" | "cannot_sign" | "typed_name" | "skipped";

export interface FlowState {
  direction: Direction;
  step: Step;
  roster: Roster;
  now: number;
  /** null on the self-service kiosk: nobody is on duty at the tablet. */
  staffId: string | null;
  /**
   * The self-service kiosk: a parent asks for their children and the staff member who has them
   * hands them over. PINs are always asked for when the adult has one, nobody witnesses here
   * (the releasing staff member does), and restrictions are never shown on the parent's screen.
   */
  selfService: boolean;
  adultPins: boolean;
  /** A pickup waits for a photo with a face (the school's rules; optional goes ahead without one). */
  photoRequired: boolean;
  policy: SignaturePolicy;
  children: Child[];
  /** Children offered on the siblings step (brothers and sisters, or every child on a pass). */
  offer: Child[];
  adult: CollectingAdult | null;
  authMethod: AuthMethod | null;
  someone: { name: string; idType: IdType; last4: string } | null;
  witnessFor: WitnessPurpose | null;
  witnessStaffId: string | null;
  restrictions: Restriction[];
  /** Restrictions whose subject_name matches the typed name of "someone else". */
  nameMatches: Restriction[];
  restrictionAck: boolean;
  signature: SignatureState;
  typedName: string | null;
  pinFailures: number;
  codeFailures: number;
  fromQr: boolean;
  error: string | null;
}

export type Action =
  | { type: "select_child"; childId: string }
  /** The door tablet's home screen already picked the children (several at once): straight to "who". */
  | { type: "choose_children"; childIds: string[] }
  | { type: "toggle_child"; childId: string }
  | { type: "confirm_children" }
  | { type: "qr_ok"; adultId: string; childIds: string[] }
  | { type: "error"; message: string }
  | { type: "choose_adult"; adultId: string }
  | { type: "pin_result"; ok: boolean }
  | { type: "choose_code" }
  | { type: "code_result"; delegation: { id: string; child_ids: string[]; delegate_adult_id: string } | null }
  | { type: "choose_someone" }
  | { type: "someone_details"; name: string; idType: IdType; last4: string }
  | { type: "witness_result"; staffId: string | null }
  | { type: "ack_restriction" }
  | { type: "stroke"; enough: boolean }
  | { type: "cannot_sign" }
  | { type: "typed_name_start" }
  | { type: "typed_name"; name: string }
  | { type: "skip_signature" }
  | { type: "submit" }
  | { type: "seal_failed"; message: string }
  | { type: "back" }
  | { type: "clear_error" };

export interface StartOptions {
  direction: Direction;
  roster: Roster;
  staffId: string | null;
  selfService?: boolean;
  /** The school's rules; pickup_signature and pickup_photo are optional for servers that predate them (required). */
  settings: { dropoff_signature: SignaturePolicy; adult_pins: boolean; pickup_signature?: "required" | "optional"; pickup_photo?: "required" | "optional" };
  /** Site state; California keeps a full in/out register, so drop-off signatures are required. */
  state?: string;
  now?: number;
}

export function signaturePolicy(direction: Direction, dropoff: SignaturePolicy, state?: string, pickup: "required" | "optional" = "required"): SignaturePolicy {
  // California requires a full signature at drop-off and pickup (22 CCR §101229.1), whatever the rules.
  if (state === "CA") return "required";
  return direction === "pickup" ? pickup : dropoff;
}

export function start(o: StartOptions): FlowState {
  return {
    direction: o.direction, step: "find", roster: o.roster, now: o.now ?? Date.now(), staffId: o.staffId,
    selfService: o.selfService ?? false,
    adultPins: o.settings.adult_pins, policy: signaturePolicy(o.direction, o.settings.dropoff_signature, o.state, o.settings.pickup_signature),
    photoRequired: o.direction === "pickup" && (o.settings.pickup_photo ?? "required") === "required",
    children: [], offer: [], adult: null, authMethod: null, someone: null, witnessFor: null, witnessStaffId: null,
    restrictions: [], nameMatches: [], restrictionAck: false, signature: "none", typedName: null, pinFailures: 0,
    codeFailures: 0, fromQr: false, error: null,
  };
}

const MSG = {
  pin: "That PIN did not match. Please try again.",
  pinHelp: "Please ask a member of staff to help you.",
  code: "That code did not work. Please check it and try again.",
  codeScope: (names: string) => `This code does not cover ${names}. Go back to change the children.`,
  codeBarred: "This code cannot be used here. Please ask a member of staff.",
  witnessSame: "A second member of staff needs to enter their own PIN.",
  witnessWrong: "That staff PIN did not match. Please try again.",
  name: "Please type your full name.",
  last4: "Enter the last four letters or numbers of the ID.",
  qrNone: "This pass cannot be used for these children today. Please ask a member of staff.",
} as const;

const names = (cs: Child[]) => cs.map((c) => c.first).join(" and ");

/** Adults allowed for every selected child, never a restricted one. */
export function tilesFor(s: FlowState): CollectingAdult[] {
  const [first, ...rest] = s.children;
  if (!first) return [];
  const others = rest.map((c) => new Set(allowedAdults(s.roster, c.id, s.now).map((a) => a.id)));
  return allowedAdults(s.roster, first.id, s.now).filter((a) => others.every((set) => set.has(a.id)));
}

function restrictionsOf(s: FlowState, children: Child[]): Restriction[] {
  return children.flatMap((c) => restrictionsFor(s.roster, c.id, s.now));
}

function withChildren(s: FlowState, children: Child[]): FlowState {
  return { ...s, children, restrictions: restrictionsOf(s, children), restrictionAck: false };
}

/** After the children are settled: a QR pass already named the adult; everyone else picks a tile. */
function afterChildren(s: FlowState): FlowState {
  if (s.fromQr && s.adult) {
    const ok = s.children.filter((c) => allowedAdults(s.roster, c.id, s.now).some((a) => a.id === s.adult!.id));
    if (!ok.length) return { ...start0(s), error: MSG.qrNone };
    return { ...withChildren(s, ok), step: "sign", error: null };
  }
  return { ...s, step: "who", error: null };
}

/** Back to the first step, keeping the session settings. */
function start0(s: FlowState): FlowState {
  return start({ direction: s.direction, roster: s.roster, staffId: s.staffId, selfService: s.selfService, now: s.now, settings: { dropoff_signature: s.policy, adult_pins: s.adultPins, pickup_signature: s.policy === "optional" ? "optional" : "required", pickup_photo: s.photoRequired ? "required" : "optional" } });
}

function resetAdult(s: FlowState): FlowState {
  return {
    ...s, adult: null, authMethod: null, someone: null, witnessFor: null, witnessStaffId: null, nameMatches: [],
    restrictionAck: false, signature: "none", typedName: null, pinFailures: 0, error: null,
  };
}

function afterWitness(s: FlowState): FlowState {
  switch (s.witnessFor) {
    case "cannot_sign": return { ...s, step: "sign", signature: "cannot_sign", witnessFor: null, error: null };
    case "typed_name": return { ...s, step: "sign", signature: "typed_name", witnessFor: null, error: null };
    default: return { ...s, step: "sign", witnessFor: null, error: null };
  }
}

export function reduce(s: FlowState, a: Action): FlowState {
  switch (a.type) {
    case "select_child": {
      const child = s.roster.children.find((c) => c.id === a.childId);
      if (!child) return s;
      const sibs = siblings(s.roster, child.id, s.now);
      const next = withChildren({ ...resetAdult(s), fromQr: false }, [child]);
      return sibs.length ? { ...next, step: "siblings", offer: [child, ...sibs] } : afterChildren({ ...next, offer: [] });
    }
    case "choose_children": {
      const kids = a.childIds.map((id) => s.roster.children.find((c) => c.id === id)).filter((c): c is Child => !!c);
      if (!kids.length) return s;
      return afterChildren({ ...withChildren({ ...resetAdult(s), fromQr: false }, kids), offer: [] });
    }
    case "toggle_child": {
      if (s.step !== "siblings") return s;
      const has = s.children.some((c) => c.id === a.childId);
      const picked = has
        ? s.children.filter((c) => c.id !== a.childId)
        : s.offer.filter((c) => c.id === a.childId || s.children.some((x) => x.id === c.id));
      return withChildren(s, picked);
    }
    case "confirm_children":
      if (s.step !== "siblings" || !s.children.length) return s;
      return afterChildren(s);
    case "qr_ok": {
      const adult = s.roster.adults.find((x) => x.id === a.adultId);
      const kids = s.roster.children.filter((c) => a.childIds.includes(c.id));
      // A pass never overrides the rules: the adult must still be allowed (and not restricted).
      const allowed = kids.filter((c) => allowedAdults(s.roster, c.id, s.now).some((x) => x.id === a.adultId));
      if (!adult || !allowed.length) return { ...s, error: MSG.qrNone };
      const tile = allowedAdults(s.roster, allowed[0]!.id, s.now).find((x) => x.id === a.adultId)!;
      const next = withChildren({ ...resetAdult(s), adult: tile, authMethod: "qr", fromQr: true }, allowed);
      return allowed.length > 1 ? { ...next, step: "siblings", offer: allowed } : afterChildren({ ...next, offer: [] });
    }
    case "error":
      return { ...s, error: a.message };
    case "choose_adult": {
      if (s.step !== "who") return s;
      const adult = tilesFor(s).find((x) => x.id === a.adultId);
      if (!adult) return s;
      if ((s.adultPins || s.selfService) && adult.pin_hash) return { ...s, adult, step: "adult_pin", pinFailures: 0, error: null };
      return { ...s, adult, authMethod: "tile", step: "sign", error: null };
    }
    case "pin_result": {
      if (s.step !== "adult_pin") return s;
      if (a.ok) return { ...s, authMethod: "pin", step: "sign", error: null };
      const pinFailures = s.pinFailures + 1;
      if (pinFailures >= 3) return { ...resetAdult(s), step: "who", error: MSG.pinHelp };
      return { ...s, pinFailures, error: MSG.pin };
    }
    case "choose_code":
      if (s.step !== "who") return s;
      return s.codeFailures >= 5 ? { ...s, error: MSG.pinHelp } : { ...resetAdult(s), step: "code" };
    case "code_result": {
      if (s.step !== "code") return s;
      const d = a.delegation;
      if (!d) {
        // Rate-limits guessing at the door: five wrong codes and staff take over.
        const codeFailures = s.codeFailures + 1;
        if (codeFailures >= 5) return { ...resetAdult(s), codeFailures, step: "who", error: MSG.pinHelp };
        return { ...s, codeFailures, error: MSG.code };
      }
      const uncovered = s.children.filter((c) => !d.child_ids.includes(c.id));
      if (uncovered.length) return { ...s, error: MSG.codeScope(names(uncovered)) };
      const barred = s.restrictions.some((r) => r.subject_adult_id === d.delegate_adult_id);
      const a0 = s.roster.adults.find((x) => x.id === d.delegate_adult_id);
      if (barred || !a0) return { ...s, error: MSG.codeBarred };
      const adult: CollectingAdult = { ...a0, relation: "delegate", kind: "delegate", delegation_id: d.id };
      return { ...s, adult, authMethod: "code", step: "sign", error: null };
    }
    case "choose_someone":
      return s.step === "who" ? { ...resetAdult(s), step: "someone" } : s;
    case "someone_details": {
      if (s.step !== "someone") return s;
      const name = a.name.trim().replace(/\s+/g, " ").normalize("NFC");
      const last4 = a.last4.trim().toUpperCase();
      if (name.length < 3 || !name.includes(" ") || name.length > 120) return { ...s, error: MSG.name };
      if (!/^[A-Z0-9]{4}$/.test(last4)) return { ...s, error: MSG.last4 };
      const lower = name.toLowerCase();
      const nameMatches = s.restrictions.filter((r) => r.subject_name && r.subject_name.trim().toLowerCase() === lower);
      // Self-service: the staff member who releases the child checks the ID and is the witness.
      if (s.selfService) return { ...s, someone: { name, idType: a.idType, last4 }, adult: null, authMethod: "id_check", nameMatches, step: "sign", error: null };
      return {
        ...s, someone: { name, idType: a.idType, last4 }, adult: null, authMethod: "id_check", nameMatches,
        step: "witness", witnessFor: "unlisted_adult", error: null,
      };
    }
    case "witness_result": {
      if (s.step !== "witness") return s;
      if (!a.staffId) return { ...s, error: MSG.witnessWrong };
      if (a.staffId === s.staffId) return { ...s, error: MSG.witnessSame };
      return afterWitness({ ...s, witnessStaffId: a.staffId });
    }
    case "ack_restriction":
      return { ...s, restrictionAck: true };
    case "stroke":
      if (s.step !== "sign" || (s.signature !== "none" && s.signature !== "drawn" && s.signature !== "skipped")) return s;
      return { ...s, signature: a.enough ? "drawn" : "none" };
    case "cannot_sign":
      if (s.step !== "sign") return s;
      return s.witnessStaffId || s.selfService ? { ...s, signature: "cannot_sign" } : { ...s, step: "witness", witnessFor: "cannot_sign", error: null };
    case "typed_name_start":
      return s.step === "sign" ? { ...s, step: "typed_name", error: null } : s;
    case "typed_name": {
      if (s.step !== "typed_name") return s;
      const name = a.name.trim().replace(/\s+/g, " ").normalize("NFC");
      if (name.length < 3 || name.length > 120) return { ...s, error: MSG.name };
      const next = { ...s, typedName: name };
      return s.witnessStaffId || s.selfService ? { ...next, step: "sign", signature: "typed_name", error: null } : { ...next, step: "witness", witnessFor: "typed_name", error: null };
    }
    case "skip_signature":
      return s.step === "sign" && s.policy === "optional" ? { ...s, signature: "skipped" } : s;
    case "submit": {
      if (s.step !== "sign") return s;
      const gaps = missing(s);
      return gaps.length ? { ...s, error: gaps[0]! } : { ...s, step: "sealing", error: null };
    }
    case "seal_failed":
      return { ...s, step: "sign", error: a.message };
    case "clear_error":
      return { ...s, error: null };
    case "back":
      return back(s);
  }
}

function back(s: FlowState): FlowState {
  switch (s.step) {
    case "siblings": return { ...start0(s) };
    case "who": return s.offer.length ? { ...s, step: "siblings", error: null } : start0(s);
    case "adult_pin": case "code": case "someone": return { ...resetAdult(s), step: "who" };
    case "witness":
      if (s.witnessFor === "unlisted_adult") return { ...s, step: "someone", witnessFor: null, error: null };
      if (s.witnessFor === "typed_name") return { ...s, step: "typed_name", witnessFor: null, error: null };
      return { ...s, step: "sign", witnessFor: null, error: null };
    case "typed_name": return { ...s, step: "sign", error: null };
    case "sign":
      if (s.fromQr) return s.offer.length ? { ...s, step: "siblings", error: null } : start0(s);
      return { ...resetAdult(s), step: "who" };
    default: return s;
  }
}

/** What still stops the handover from being sealed, as plain sentences; empty = ready. */
export function missing(s: FlowState): string[] {
  const out: string[] = [];
  if (!s.children.length) out.push("Choose a child.");
  if (!s.adult && !s.someone) out.push("Choose who is collecting.");
  if (s.someone && !s.witnessStaffId && !s.selfService) out.push("A second member of staff needs to enter their PIN.");
  if (s.restrictions.length && !s.restrictionAck && !s.selfService) out.push("Staff need to confirm they have checked the restriction.");
  const signed = s.signature === "drawn" || s.signature === "cannot_sign" || s.signature === "typed_name";
  if (s.policy === "required" && !signed) out.push("Please sign in the box.");
  if (s.policy === "optional" && s.signature === "none") out.push("Please sign in the box, or choose Skip signature.");
  return out;
}

export interface SealContext {
  siteId: string;
  deviceId: string;
  monoStart: number;
  attestation: string;
  photoSha256: string | null;
  face: FaceBox | null;
  strokeSha256: string | null;
  offline?: boolean;
  now?: number;
}

/** One pre-image per child; siblings share a group id (plan §6 step 2). Self-service: one pickup_request per child. */
export function buildEvents(s: FlowState, c: SealContext): PreImage[] {
  const gaps = missing(s);
  if (gaps.length) throw new Error(gaps.join(" "));
  const now = c.now ?? Date.now();
  // Self-service requests always carry a group id: the kiosk follows its request by it.
  const groupId = s.children.length > 1 || s.selfService ? uuidv7(now) : null;
  const requestType = s.direction === "dropoff" ? "dropoff_request" : "pickup_request";
  const drawn = s.signature === "drawn" && s.policy !== "off";
  return s.children.map((child) => {
    const pre = buildPreImage({
      siteId: c.siteId, deviceId: c.deviceId, staffId: s.selfService ? null : s.staffId, type: s.selfService ? requestType : s.direction, childId: child.id, groupId,
      adult: s.adult ? { id: s.adult.id, delegationId: s.adult.delegation_id } : null,
      claimedName: s.someone?.name ?? null,
      idCheck: s.someone ? { type: s.someone.idType, last4: s.someone.last4 } : null,
      witnessStaffId: s.witnessStaffId, authMethod: s.authMethod ?? "tile",
      photoSha256: c.photoSha256, face: c.face, strokeSha256: drawn ? c.strokeSha256 : null,
      // The typed-name alternative is also "cannot sign" (no drawn signature), witnessed by staff.
      cannotSign: s.signature === "cannot_sign",
      typedName: s.signature === "typed_name", restrictionAck: !s.selfService && s.restrictionAck && s.restrictions.length > 0,
      checkId: s.selfService && (s.authMethod === "tile" || s.someone !== null),
      offline: c.offline, attestation: c.attestation, monoStart: c.monoStart, now,
    });
    // A pickup whose signature the school made optional and the adult skipped: recorded as such.
    const extra: PreImage["flags"] = [];
    if (s.direction === "pickup" && s.signature === "skipped") extra.push("signature_skipped");
    // A pickup without a photo (optional by the school's rules, or no camera): recorded as such.
    if (s.direction === "pickup" && !c.photoSha256) extra.push("no_photo");
    // Flags are kept sorted and unique (the schema checks it).
    if (extra.length) pre.flags = [...new Set([...pre.flags, ...extra])].sort();
    // The accessible path stores the typed name; for "someone else" it is the name they gave.
    if (s.signature === "typed_name" && s.adult && s.typedName) pre.claimed_name = s.typedName;
    return pre;
  });
}
